import type { JSX } from 'react'
import { useEffect, useRef } from 'react'
import { Button } from 'primereact/button'

import type { AuditEntry } from '../../../hooks/useAuditLog.js'
import { useFocusTrap } from '../../../hooks/useFocusTrap.js'
import { formatDateTime } from '../../../utils/formatDate.js'

export type AuditLogDetailSource = 'tenant' | 'platform'

export interface AuditLogDetailDrawerProps {
  /** The selected entry to render; `null` collapses the drawer entirely. */
  entry: AuditEntry | null
  /** Fired when the user clicks Close or hits Escape. */
  onClose: () => void
  /**
   * Which audit surface the entry comes from. Defaults to `'tenant'` for
   * backwards compatibility with the UI-7a tenant audit page.
   *
   * UI-8d Task 4: the platform audit log reuses this drawer; the entry
   * shape is identical between the two surfaces so behavior is the
   * same. The prop is accepted for future-proofing (e.g. showing a
   * "Platform event" badge) and to keep both callers explicit.
   */
  source?: AuditLogDetailSource
}

/**
 * Read-only side drawer for a single audit log entry.
 *
 * Unlike `ResourceDrawer` (the create/edit drawer with save semantics),
 * this is detail-only — no Save button, no form. Rendering is gated by
 * `entry != null` so the parent page can drive open/close with
 * `setSelected(row)` / `setSelected(null)` and avoid a separate boolean.
 *
 * Field values that may be null on the audit row (actorId,
 * resourceType, resourceId, prevHash) fall back to an em-dash so the
 * row layout stays consistent.
 *
 * The payload is pretty-printed via `JSON.stringify(..., null, 2)` so
 * even nested structures stay legible at a glance.
 *
 * Accessibility: ESC closes the drawer; focus is trapped inside the
 * panel while open. Mirrors ResourceDrawer's posture without pulling
 * in the full Save-button machinery.
 */
export function AuditLogDetailDrawer({
  entry,
  onClose,
  source: _source = 'tenant',
}: AuditLogDetailDrawerProps): JSX.Element | null {
  void _source // accepted for future-proofing; same shape applies to both surfaces

  const open = entry !== null
  const trapRef = useFocusTrap<HTMLElement>(open)
  const previousFocus = useRef<HTMLElement | null>(null)

  // Capture / restore focus on open / close — keyboard users land back
  // on the row they clicked.
  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!entry) return null

  const dash = (s: string | null | undefined): string => (s == null || s === '' ? '—' : s)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        aria-hidden="true"
        onClick={onClose}
      />

      <aside
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label="Audit log entry"
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">
            Audit log entry
          </h2>
          <Button
            icon="pi pi-times"
            text
            severity="secondary"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <dl className="grid grid-cols-[12rem,1fr] gap-y-2 text-sm">
            <dt className="text-gray-500">ID</dt>
            <dd className="font-mono text-gray-900 break-all">{entry.id}</dd>

            <dt className="text-gray-500">Created at</dt>
            <dd className="text-gray-900">{formatDateTime(entry.createdAt)}</dd>

            <dt className="text-gray-500">Actor</dt>
            <dd className="font-mono text-gray-900 break-all">{dash(entry.actorId)}</dd>

            <dt className="text-gray-500">Action</dt>
            <dd className="text-gray-900">{entry.action}</dd>

            <dt className="text-gray-500">Resource type</dt>
            <dd className="text-gray-900">{dash(entry.resourceType)}</dd>

            <dt className="text-gray-500">Resource ID</dt>
            <dd className="font-mono text-gray-900 break-all">{dash(entry.resourceId)}</dd>

            <dt className="text-gray-500">Hash</dt>
            <dd className="font-mono text-xs text-gray-900 break-all">{entry.hash}</dd>

            <dt className="text-gray-500">Prev hash</dt>
            <dd className="font-mono text-xs text-gray-900 break-all">{dash(entry.prevHash)}</dd>
          </dl>

          <h3 className="mt-6 mb-2 text-xs font-semibold uppercase text-gray-500">
            Payload
          </h3>
          <pre className="rounded-sm border border-gray-200 bg-gray-50 p-3 text-xs text-gray-900 overflow-x-auto">
            {JSON.stringify(entry.payload, null, 2)}
          </pre>
        </div>
      </aside>
    </>
  )
}

export default AuditLogDetailDrawer
